import React, { useState, ReactNode } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { LeaderboardRankIcon } from './LeaderboardRankIcon';
import { LeaderboardScoreDisplay } from './LeaderboardScoreDisplay';
import type { LeaderboardEntry, LeaderboardColumn } from './LeaderboardBase';

// Premium Color Palette from PREMIUM_METRIC_PATTERNS.md
const PREMIUM_COLORS = {
  primaryGolfGreen: '#1B4332',
  secondaryGolfGreen: '#2D5A3D',
  backgroundMint: '#F0FDF4',
  accentBrightGreen: '#52B788',
  neutralGray: '#9CA3AF',
  darkText: '#374151'
};

export interface LeaderboardExpandableRowProps {
  entry: LeaderboardEntry;
  columns: LeaderboardColumn[];
  showRankIcons: boolean;
  allowExpansion: boolean;
  onEntryClick?: (entry: LeaderboardEntry) => void;
  isEven: boolean;
}

/**
 * Unified Expandable Row Component
 * 
 * Consolidates expandable row functionality used across all leaderboards:
 * - Smooth expand/collapse animations
 * - Consistent row styling and hover effects
 * - Unified click handling and state management
 * - Current user highlighting
 * - Responsive column rendering
 */
export function LeaderboardExpandableRow({
  entry,
  columns,
  showRankIcons,
  allowExpansion,
  onEntryClick,
  isEven
}: LeaderboardExpandableRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpansion = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleRowClick = () => {
    if (onEntryClick) {
      onEntryClick(entry);
    } else if (allowExpansion && entry.expandedContent) {
      setIsExpanded(!isExpanded);
    }
  };

  const getRowStyle = () => {
    const baseStyle = {
      transition: 'all 0.2s ease-in-out',
      cursor: (onEntryClick || allowExpansion) ? 'pointer' : 'default'
    };

    if (entry.isCurrentUser) {
      return {
        ...baseStyle,
        backgroundColor: PREMIUM_COLORS.backgroundMint,
        borderLeft: `4px solid ${PREMIUM_COLORS.accentBrightGreen}`
      };
    }

    if (isEven) {
      return {
        ...baseStyle,
        backgroundColor: 'rgba(243, 244, 246, 0.3)'
      };
    }

    return baseStyle;
  };

  const renderCellContent = (column: LeaderboardColumn, entry: LeaderboardEntry) => {
    // If column has custom render function, use it
    if (column.render) {
      return column.render(entry.metadata?.[column.key] || entry[column.key as keyof LeaderboardEntry], entry);
    }

    // Handle special cases for common column types
    switch (column.key) {
      case 'name':
        return (
          <div className="flex items-center gap-3">
            {entry.avatar ? <img 
              src={entry.avatar} 
              alt={entry.name}
              className="w-8 h-8 rounded-full object-cover"
                            /> : null}
            <div>
              <div className="font-medium" style={{ color: PREMIUM_COLORS.primaryGolfGreen }}>
                {entry.name}
              </div>
              {entry.isCurrentUser ? <div className="text-xs" style={{ color: PREMIUM_COLORS.accentBrightGreen }}>
                You
                                     </div> : null}
            </div>
          </div>
        );

      case 'score':
        return (
          <LeaderboardScoreDisplay
            score={entry.score}
            scoreDifferential={entry.scoreDifferential}
            showDifferential
            size="md"
            variant="default"
          />
        );

      default:
        // Generic value display
        const value = entry.metadata?.[column.key] || entry[column.key as keyof LeaderboardEntry];
        return (
          <span style={{ color: PREMIUM_COLORS.darkText }}>
            {value || '-'}
          </span>
        );
    }
  };

  return (
    <>
      <TableRow 
        style={getRowStyle()}
        onClick={handleRowClick}
        className="hover:opacity-80"
      >
        {/* Rank Column */}
        {showRankIcons ? <TableCell className="text-center">
          <LeaderboardRankIcon 
            rank={entry.rank} 
            size="md" 
            showBackground={entry.rank <= 3}
          />
        </TableCell> : null}

        {/* Dynamic Columns */}
        {columns.map((column) => (
          <TableCell 
            key={column.key}
            className={`${column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'}`}
            style={{ width: column.width }}
          >
            {renderCellContent(column, entry)}
          </TableCell>
        ))}

        {/* Expand/Collapse Button */}
        {allowExpansion ? <TableCell className="w-12">
          {entry.expandedContent ? <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleExpansion}
            className="h-8 w-8 p-0 hover:bg-gray-100"
            style={{ minHeight: '44px', minWidth: '44px' }}
                                   >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" style={{ color: PREMIUM_COLORS.primaryGolfGreen }} />
                ) : (
                  <ChevronRight className="h-4 w-4" style={{ color: PREMIUM_COLORS.primaryGolfGreen }} />
                )}
                                   </Button> : null}
        </TableCell> : null}
      </TableRow>

      {/* Expanded Content Row */}
      {allowExpansion && isExpanded && entry.expandedContent ? <TableRow>
        <TableCell 
          colSpan={columns.length + (showRankIcons ? 1 : 0) + 1}
          className="p-0"
        >
          <div 
            className="border-t bg-gray-50 transition-all duration-300 ease-in-out overflow-hidden"
            style={{
                backgroundColor: PREMIUM_COLORS.backgroundMint,
                borderColor: 'rgba(27, 67, 50, 0.1)'
              }}
          >
            <div className="p-4">
              {entry.expandedContent}
            </div>
          </div>
        </TableCell>
      </TableRow> : null}
    </>
  );
}